/** Create / edit dialog for a tag-category set (react-hook-form + zod).
 *
 *  A set is a named bundle of tag-category definitions applied to a TO. PATCH
 *  full-replaces the items array, so the form always submits the complete list.
 *  Per-item advanced flags (state_category / accounting / billing / keyfield /
 *  association) are collapsed under an "Advanced" toggle; `association` is a
 *  free-form JSON object edited as text. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createSet,
  update as updateSet,
  type TagCategorySet,
  type TagCategorySetWrite,
} from '@/api/tagCategorySets';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

const RESTECH_OPTIONS = ['', 'fareharbor', 'ventrata', 'bokun', 'rezdy', 'trekksoft', 'xml', 'other'];

/** `association` is edited as JSON text; validate that it parses to an object
 *  (or is blank → null). */
const associationField = z.string().refine(
  (v) => {
    const t = v.trim();
    if (!t) return true;
    try {
      const parsed = JSON.parse(t);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch {
      return false;
    }
  },
  { message: 'Must be a JSON object, or blank.' },
);

const ItemSchema = z.object({
  code: z.string().min(1, 'Required'),
  name: z.string().min(1, 'Required'),
  sequence: z.number({ invalid_type_error: 'Required' }).int('Whole number').min(0, 'Must be ≥ 0'),
  is_multitag: z.boolean(),
  state_category: z.boolean(),
  accounting: z.boolean(),
  billing: z.boolean(),
  keyfield: z.string(),
  association: associationField,
});

const FormSchema = z.object({
  code: z.string().min(1, 'Required'),
  name: z.string().min(1, 'Required'),
  label: z.string(),
  restech: z.string(),
  description: z.string(),
  items: z.array(ItemSchema),
});

type FormValues = z.infer<typeof FormSchema>;
type ItemValues = z.infer<typeof ItemSchema>;

const EMPTY_ITEM: ItemValues = {
  code: '',
  name: '',
  sequence: 0,
  is_multitag: false,
  state_category: false,
  accounting: false,
  billing: false,
  keyfield: '',
  association: '',
};

const EMPTY: FormValues = {
  code: '',
  name: '',
  label: '',
  restech: '',
  description: '',
  items: [],
};

function toDefaults(set: TagCategorySet | null): FormValues {
  if (!set) return EMPTY;
  return {
    code: set.code ?? '',
    name: set.name ?? '',
    label: set.label ?? '',
    restech: set.restech ?? '',
    description: set.description ?? '',
    items: set.items.map((it) => ({
      code: it.code ?? '',
      name: it.name ?? '',
      sequence: it.sequence ?? 0,
      is_multitag: it.is_multitag,
      state_category: it.state_category,
      accounting: it.accounting,
      billing: it.billing,
      keyfield: it.keyfield ?? '',
      association: it.association ? JSON.stringify(it.association, null, 2) : '',
    })),
  };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  set: TagCategorySet | null;
  onClose: () => void;
}

export function TagCategorySetDialog({ open, set, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = set !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.tag_category_set',
    objectPk: set?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['tag_category_sets'] }),
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(set),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  useEffect(() => {
    if (open) {
      history.reset();
      reset(toDefaults(set));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, set?.id]);

  const mutation = useMutation({
    mutationFn: (body: TagCategorySetWrite) =>
      isEdit ? updateSet(set.id, body) : createSet(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Tag category set updated.' : 'Tag category set created.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_category_sets'] });
      onClose();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    mutation.mutate({
      code: v.code.trim(),
      name: v.name.trim(),
      label: v.label.trim(),
      restech: v.restech,
      description: v.description.trim(),
      items: v.items.map((it) => {
        const assoc = it.association.trim();
        return {
          code: it.code.trim(),
          name: it.name.trim(),
          sequence: it.sequence,
          is_multitag: it.is_multitag,
          state_category: it.state_category,
          accounting: it.accounting,
          billing: it.billing,
          keyfield: it.keyfield.trim(),
          association: assoc ? (JSON.parse(assoc) as Record<string, unknown>) : null,
        };
      }),
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={isEdit ? `Edit set · ${set.name}` : 'New tag category set'}
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create set'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            {...register('code')}
            label="Code"
            size="small"
            fullWidth
            error={Boolean(errors.code)}
            helperText={errors.code?.message ?? 'Stable machine identifier.'}
          />
          <TextField
            {...register('name')}
            label="Name"
            size="small"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            {...register('label')}
            label="Label"
            size="small"
            fullWidth
            error={Boolean(errors.label)}
            helperText={errors.label?.message}
          />
          <Controller
            control={control}
            name="restech"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Restech"
                size="small"
                fullWidth
                error={Boolean(errors.restech)}
                helperText={errors.restech?.message}
              >
                {RESTECH_OPTIONS.map((r) => (
                  <MenuItem key={r || 'none'} value={r}>
                    {r || <em>None</em>}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
        />

        <Divider />
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Categories
          </Typography>
          {fields.length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              No categories. Saving replaces the set&apos;s full category list.
            </Alert>
          )}
          <Stack spacing={1.5}>
            {fields.map((f, i) => (
              <ItemRow key={f.id} index={i} control={control} register={register} onRemove={() => remove(i)} errors={errors} />
            ))}
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => append({ ...EMPTY_ITEM, sequence: fields.length })}
          >
            Add category
          </Button>
        </Box>
      </Box>
      )}
    </AppDialog>
  );
}

// ---------------------------------------------------------------------------
// One category row. Core fields (code / name / sequence / multitag) are always
// shown; the rest fold under an "Advanced" toggle.
// ---------------------------------------------------------------------------

interface ItemRowProps {
  index: number;
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  onRemove: () => void;
}

function ItemRow({ index, control, register, errors, onRemove }: ItemRowProps) {
  const [advanced, setAdvanced] = useState(false);
  const itemErrors = errors.items?.[index];

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Controller
          control={control}
          name={`items.${index}.code`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Code"
              size="small"
              sx={{ width: 160 }}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name={`items.${index}.name`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Name"
              size="small"
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <TextField
          {...register(`items.${index}.sequence`, { valueAsNumber: true })}
          label="Seq"
          size="small"
          type="number"
          sx={{ width: 90 }}
          error={Boolean(itemErrors?.sequence)}
        />
        <Controller
          control={control}
          name={`items.${index}.is_multitag`}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
              control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
              label="Multitag"
            />
          )}
        />
        <IconButton size="small" aria-label="Remove category" onClick={onRemove} sx={{ mt: 0.5 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Button
        size="small"
        startIcon={advanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setAdvanced((v) => !v)}
        sx={{ mt: 0.5 }}
      >
        Advanced
      </Button>
      <Collapse in={advanced} unmountOnExit>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Controller
              control={control}
              name={`items.${index}.state_category`}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="State category"
                />
              )}
            />
            <Controller
              control={control}
              name={`items.${index}.accounting`}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Accounting"
                />
              )}
            />
            <Controller
              control={control}
              name={`items.${index}.billing`}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Billing"
                />
              )}
            />
          </Stack>
          <Controller
            control={control}
            name={`items.${index}.keyfield`}
            render={({ field }) => (
              <TextField {...field} label="Key field" size="small" sx={{ width: 240 }} />
            )}
          />
          <Controller
            control={control}
            name={`items.${index}.association`}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Association (JSON object)"
                size="small"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? 'Optional. A JSON object, or blank.'}
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 } } }}
              />
            )}
          />
        </Stack>
      </Collapse>
    </Paper>
  );
}
